import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/common/components/ui/dropdown-menu';
import {
  LucideChevronDown,
  LucideExternalLink,
  LucideLogOut,
  LucideUsersRound,
  LucideX,
  UserRound
} from 'lucide-react';
import { LoginButton } from '~/shell/components/LoginButton';
import { cn } from '~/common/utils/styleUtil';
import { getLinkRel } from '~/common/utils/urlUtil';
import { NavDropDownMenuItem } from '~/shell/components/NavDropDownMenuItem';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useFetcher } from 'react-router';
import { useBasePath } from '~/common/hooks/useBasePath';
import { formatCustomerNumber } from '~/common/utils/sessionCustomerUtil';
import { logoutCart } from '../../features/cart/actions/cartActions';
import { Button } from '~/common/components/ui/button';
import { getLanguageValue, webshopConfig } from '~/config/webshopConfig';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { useScrollLock } from 'usehooks-ts';
import type { IWebshopLoginInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { useDeploymentInfo } from '~/config/useDeploymentInfo';
import { Media } from '~/media.ts';
import { DEPLOYMENT_ENVIRONMENTS } from '../../common/constants/ppConstants';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';

export interface UserNavigationProps {
  loginInfo?: IWebshopLoginInfoDtoData | null;
  isOciSession?: boolean;
}

type SubMenu = 'delivery' | 'settings' | null;

interface UserNavigationItem {
  label: string;
  link?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  subMenu?: SubMenu;
  children?: UserNavigationItem[];
  onClick?: () => void;
}

export function UserNavigation({ loginInfo, isOciSession }: Readonly<UserNavigationProps>) {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const fetcher = useFetcher();
  const [openSubMenu, setOpenSubMenu] = useState<SubMenu>('delivery');
  const { path } = useBasePath();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { isAgh } = useBasePath();
  const { lock, unlock } = useScrollLock({ autoLock: false });
  const { DEPLOYMENT_ENVIRONMENT } = useDeploymentInfo();

  const handleLogout = useCallback(() => {
    if (!isAgh) {
      logoutCart();
    }

    const formData = new FormData();
    formData.append(FORM_FIELDS.RedirectTo, path);
    fetcher.submit(formData, { method: 'post', action: '/infra/auth/logout' });
  }, [fetcher, isAgh, path]);

  const handleAccountSwitch = useCallback(() => {
    fetcher.submit(null, { method: 'post', action: `/infra/auth/switch-account` });
  }, [fetcher]);

  const handleChangePassword = useCallback(() => {
    fetcher.submit(null, { method: 'post', action: `/infra/auth/update-password` });
  }, [fetcher]);

  const portalLink = useMemo(() => {
    let link = 'https://portal.transgourmet.ch';
    if (DEPLOYMENT_ENVIRONMENT === DEPLOYMENT_ENVIRONMENTS.PREVIEW) {
      link = 'https://portalpreview.transgourmet.ch';
    } else if (DEPLOYMENT_ENVIRONMENT === DEPLOYMENT_ENVIRONMENTS.STAGE) {
      link = 'https://portalstage.transgourmet.ch';
    } else if (DEPLOYMENT_ENVIRONMENT === DEPLOYMENT_ENVIRONMENTS.TEST) {
      link = 'https://portaltest.transgourmet.ch';
    } else if (DEPLOYMENT_ENVIRONMENT === DEPLOYMENT_ENVIRONMENTS.DEVELOPMENT) {
      link = 'http://localhost:3001';
    }

    return link;
  }, [DEPLOYMENT_ENVIRONMENT]);

  const navigationItems: UserNavigationItem[] = useMemo(() => {
    const items: UserNavigationItem[] = [];

    if (loginInfo?.isAccountSwitchEnabled) {
      items.push({
        label: t('Konto ändern'),
        onClick: handleAccountSwitch,
        startIcon: <LucideUsersRound />
      });
    }
    if (!isAgh) {
      items.push({
        label: t('Lieferung'),
        subMenu: 'delivery',
        children: [
          { label: t('Liefertage'), link: `${path}/deliverydays` },
          {
            label: t('Bestellschlusszeiten'),
            link: getLanguageValue(i18n.language, webshopConfig.endOrderTimeLink),
            endIcon: <LucideExternalLink size={16} className={'text-secondary'} />
          },
          { label: t('Aufträge'), link: `${path}/orders` },
          { label: t('Lieferscheine'), link: `${path}/shipments` },
          { label: t('Rechnungen'), link: `${path}/invoices` }
        ]
      });
    }
    items.push({
      label: t('Kundenportal'),
      link: portalLink,
      endIcon: <LucideExternalLink size={16} className={'text-secondary'} />
    });

    if (!isAgh) {
      items.push({ label: t('Inventur'), link: `${path}/inventory` });
    }

    items.push(
      { label: t('Passwort ändern'), onClick: handleChangePassword },
      { label: t('Logout'), onClick: handleLogout, startIcon: <LucideLogOut /> }
    );

    return items;
  }, [handleAccountSwitch, handleChangePassword, handleLogout, i18n.language, isAgh, loginInfo, path, portalLink, t]);

  const handleDrawerClose = useCallback(() => {
    unlock();
    setIsDrawerOpen(false);
  }, [unlock]);

  const handleDrawerOpen = useCallback(() => {
    lock();
    setIsDrawerOpen(true);
  }, [lock]);

  if (!loginInfo) {
    return <LoginButton showAghTooltip={isAgh} />;
  }

  const customerNumber = formatCustomerNumber(loginInfo);

  const renderDropDownItem = (item: UserNavigationItem, level: number, lastItem: boolean) => {
    let dropDownItem;

    if (item.link) {
      if (item.link.startsWith('http')) {
        dropDownItem = (
          <TrackNavigationClick label={item.label}>
            <a href={item.link} target={'_blank'} rel={getLinkRel(item.link)}>
              <NavDropDownMenuItem
                inset={level > 0}
                className={'flex justify-between'}
                data-testid={`user-navigation-${item.label}`}
              >
                <div className={'flex gap-2'}>
                  {item.startIcon}
                  {item.label}
                </div>
                <div>{item.endIcon}</div>
              </NavDropDownMenuItem>
            </a>
          </TrackNavigationClick>
        );
      } else {
        dropDownItem = (
          <TrackNavigationClick label={item.label}>
            <NavLink
              onClick={() => setOpen(false)}
              className={({ isActive }) => cn(isActive && 'text-primary')}
              to={item.link}
            >
              <NavDropDownMenuItem inset={level > 0} data-testid={`user-navigation-${item.label}`}>
                <div className={'flex gap-2'}>
                  {item.startIcon}
                  {item.label}
                </div>
                <div>{item.endIcon}</div>
              </NavDropDownMenuItem>
            </NavLink>
          </TrackNavigationClick>
        );
      }
    } else if (item.onClick) {
      dropDownItem = (
        <TrackNavigationClick label={item.label}>
          <NavDropDownMenuItem
            inset={level > 0}
            onClick={item.onClick}
            className={`flex justify-between ${cn(lastItem && 'border-b-0')}`}
            data-testid={`user-navigation-${item.label}`}
          >
            <div className={'flex gap-2'}>
              {item.startIcon}
              {item.label}
            </div>
            <div>{item.endIcon}</div>
          </NavDropDownMenuItem>
        </TrackNavigationClick>
      );
    } else if (item.subMenu) {
      dropDownItem = (
        <NavDropDownMenuItem
          inset={level > 0}
          onClick={e => {
            e.preventDefault();
            setOpenSubMenu(prevState => (prevState === item.subMenu ? null : item.subMenu!));
          }}
          className={'flex justify-between'}
          data-testid={`user-navigation-${item.label}`}
        >
          <div className={'flex gap-2'}>
            {item.startIcon}
            {item.label}
          </div>
          <div>
            <LucideChevronDown size={16} className={cn(openSubMenu === item.subMenu && 'rotate-180')} />
          </div>
        </NavDropDownMenuItem>
      );
    }

    return (
      <Fragment key={`${item.label}-${level}`}>
        {dropDownItem}
        {item.children &&
          openSubMenu === item.subMenu &&
          item.children.map(item => renderDropDownItem(item, level + 1, false))}
      </Fragment>
    );
  };

  const renderDrawerItem = (item: UserNavigationItem, level = 0) => {
    let itemElement;

    if (item.link) {
      if (item.link.startsWith('http')) {
        itemElement = (
          <TrackNavigationClick label={item.label}>
            <a
              href={item.link}
              target={'_blank'}
              rel={getLinkRel(item.link)}
              className={'p-3 flex justify-between border-b'}
              data-testid={`user-navigation-${item.label}`}
            >
              <div className={'flex gap-2'}>
                {item.startIcon}
                {item.label}
              </div>
              <div>{item.endIcon}</div>
            </a>
          </TrackNavigationClick>
        );
      } else {
        itemElement = (
          <TrackNavigationClick label={item.label}>
            <NavLink
              onClick={() => handleDrawerClose()}
              className={({ isActive }) => cn('flex gap-2 justify-between p-3 border-b', isActive && 'text-primary')}
              data-testid={`user-navigation-${item.label}`}
              to={item.link}
            >
              <div className={'flex gap-2'}>
                {item.startIcon}
                {item.label}
              </div>
              <div>{item.endIcon}</div>
            </NavLink>
          </TrackNavigationClick>
        );
      }
    } else if (item.onClick) {
      itemElement = (
        <TrackNavigationClick label={item.label}>
          <button type="button"
            onClick={item.onClick}
            className={'p-3 border-b w-full text-left flex justify-between items-center hover:primary'}
            data-testid={`user-navigation-${item.label}`}
          >
            <div className={'flex gap-2'}>
              {item.startIcon}
              {item.label}
            </div>
            <div>{item.endIcon}</div>
          </button>
        </TrackNavigationClick>
      );
    } else if (item.subMenu) {
      itemElement = (
        <button type="button"
          onClick={e => {
            e.preventDefault();
            setOpenSubMenu(prevState => (prevState === item.subMenu ? null : item.subMenu!));
          }}
          className={'p-3 border-b w-full text-left flex justify-between items-center hover:primary'}
          data-testid={`user-navigation-${item.label}`}
        >
          <div className={'flex gap-2'}>
            {item.startIcon}
            {item.label}
          </div>
          <div>
            <LucideChevronDown size={16} className={cn(openSubMenu === item.subMenu && 'rotate-180')} />
          </div>
        </button>
      );
    }

    return (
      <Fragment key={`${item.label}-${level}`}>
        <div style={{ paddingLeft: level > 0 ? `${level * 16}px` : 0 }} className={'w-full'}>
          {itemElement}
        </div>
        {item.children && openSubMenu === item.subMenu && item.children.map(item => renderDrawerItem(item, level + 1))}
      </Fragment>
    );
  };

  return (
    <>
      <Media lessThan="desktop">
        <div>
          {isDrawerOpen ? (
            <div className={'absolute inset-0 h-[100dvh] max-h-dvh bg-white z-50 flex flex-col'}>
              <div className={'flex justify-between shrink-0'}>
                <div className={'p-4'}>
                  <p className={'text-left font-bold'}>{loginInfo.customerName}</p>
                  <p className={'text-left'}>
                    {customerNumber}
                    {isOciSession && <span className={'ml-2'}>OCI</span>}
                  </p>
                </div>
                <Button className={'mt-2'} variant={'ghost'} onClick={handleDrawerClose}>
                  <LucideX />
                </Button>
              </div>
              <div className={'flex-1 overflow-y-auto text-sm pb-24'}>
                <div className={'px-4 text-secondary'}>
                  <div>{loginInfo.companyName}</div>
                  <div data-testid={'user-navigation-customer-name'}>{loginInfo.customerName}</div>
                  <div data-testid={'user-navigation-customer-number'}>{customerNumber}</div>
                </div>
                <div className={'border-t my-4'}>{navigationItems.map(item => renderDrawerItem(item, 0))}</div>
              </div>
            </div>
          ) : (
            <Button variant={'ghost'} className={'px-2'} onClick={handleDrawerOpen}>
              <LucideUsersRound />
            </Button>
          )}
        </div>
      </Media>
      <Media greaterThanOrEqual="desktop">
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
          <DropdownMenuTrigger data-testid={'user-navigation'}>
            <div
              className={cn(
                'cursor-pointer flex items-center gap-1 px-2 hover:text-primary h-10',
                open && 'text-primary'
              )}
            >
              <UserRound className="xl:size-[16px]" />
              <div className={'hidden xl:block'}>
                <div
                  className={'text font-bold leading-none max-w-[80px] xl:max-w-none text-ellipsis overflow-hidden'}
                  data-testid={'user-navigation-customer-name'}
                >
                  {loginInfo.customerName}
                </div>
                <div className={'text-left text-xs leading-none'} data-testid={'user-navigation-customer-number'}>
                  {customerNumber}
                  {isOciSession && <span className={'ml-2'}>OCI</span>}
                </div>
              </div>
              <div>
                <LucideChevronDown size={14} className={cn(open && 'rotate-180')} />
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={'p-4 max-h-[calc(100dvh-150px)] overflow-y-auto'}>
            <div className={'p-2 text-sm text-secondary'}>
              <div>{loginInfo.companyName}</div>
              <div>{loginInfo.customerName}</div>
              <div>{customerNumber}</div>
            </div>
            {navigationItems.map((item, index) => renderDropDownItem(item, 0, navigationItems.length === index + 1))}
          </DropdownMenuContent>
        </DropdownMenu>
      </Media>
    </>
  );
}
